import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, FileSignature, ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";

const STATUS_LABEL = {
  not_configured: "Nessun accordo attivo configurato",
  not_required: "Non richiesto per il tuo piano",
  pending: "Accettazione richiesta",
  new_version_required: "Nuova versione da accettare",
  accepted: "Accettato",
  delegation_pending_verification: "Richiesta delega in verifica — nessuna accettazione registrata",
};

const fmtRome = (iso) =>
  iso ? new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", dateStyle: "long", timeStyle: "medium" }) + " (Europe/Rome)" : "—";

export default function ClientDpaAgreement() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [acceptances, setAcceptances] = useState([]);
  const [form, setForm] = useState({ first_name: "", last_name: "", qualifica: "legale_rappresentante", delegation_reference: "", password: "", authority_declared: false, contract_accepted: false,
    legal: { legal_name: "", legal_address: "", legal_zip: "", legal_city: "", legal_province: "", piva: "" } });
  const [preview, setPreview] = useState(null);
  const updLegal = (k) => (e) => { setPreview(null); setForm((f) => ({ ...f, legal: { ...f.legal, [k]: e.target.value } })); };
  const [busy, setBusy] = useState(false);
  const idem = useMemo(() => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now()) + Math.random()), []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dpa/agreement");
      setData(data);
      if (data.legal_prefill) setForm((f) => ({ ...f, legal: { ...f.legal, ...Object.fromEntries(Object.entries(data.legal_prefill).filter(([, v]) => v)) } }));
      if (data.delegation) setForm((f) => ({ ...f, qualifica: "delegato", first_name: data.delegation.first_name, last_name: data.delegation.last_name, delegation_reference: data.delegation.delegation_reference }));
      if (user?.role === "client") {
        const r = await api.get("/dpa/acceptances");
        setAcceptances(r.data.acceptances || []);
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [user?.role]);

  useEffect(() => { load(); }, [load]);

  const v = data?.active_version;
  const status = data?.status;
  const canAccept = user?.role === "client" && user?.plan === "pro" && ["pending", "new_version_required", "delegation_pending_verification"].includes(status);
  const delegationVerified = data?.delegation?.status === "verified";
  const delegationPending = data?.delegation?.status === "pending";
  const upd = (k) => (e) => { setPreview(null); setForm((f) => ({ ...f, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e })); };
  const identityPayload = () => ({ version_id: v.version_id, version_sha256: v.sha256, first_name: form.first_name, last_name: form.last_name, qualifica: form.qualifica, delegation_reference: form.delegation_reference || null, legal: form.legal });

  const doPreview = async () => {
    setBusy(true);
    try { const { data: p } = await api.post("/dpa/preview", identityPayload()); setPreview(p); toast.success("Anteprima generata: controlla il documento compilato"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const requestDelegation = async () => {
    setBusy(true);
    try { await api.post("/dpa/delegation-request", { first_name: form.first_name, last_name: form.last_name, delegation_reference: form.delegation_reference }); toast.success("Richiesta di verifica della delega inviata allo staff"); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/dpa/accept", {
        ...identityPayload(),
        password: form.password,
        authority_declared: form.authority_declared,
        contract_accepted: form.contract_accepted,
        preview_sha256: preview.compiled_sha256,
        challenge: preview.challenge,
        idempotency_key: idem,
        ui_version: data.ui_version,
        client_time: new Date().toISOString(),
      });
      toast.success(res.already_accepted ? "Accordo già accettato in precedenza" : "Accordo accettato");
      setForm((f) => ({ ...f, password: "" }));
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && (d?.code === "version_changed" || d?.code === "preview_mismatch")) {
        toast.error("La versione o i dati sono cambiati: ricarico il documento, rileggi l'anteprima prima di accettare.");
        setPreview(null); load();
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const download = async (url, name) => {
    try {
      const r = await api.get(url, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = name;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!data) return <div className="p-6 text-sm text-neutral-500">Caricamento…</div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="dpa-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><FileSignature className="h-6 w-6" /> Accordo di nomina a responsabile (art. 28 GDPR)</h1>
        <p className="text-sm text-neutral-500 mt-1">Regola il trattamento, da parte di Marketing Parrucchieri, dei contenuti che il salone invia per la lavorazione. È distinto dai consensi marketing, cookie e privacy, che restano invariati.</p>
      </div>

      <div className={`rounded-xl border p-4 text-sm ${["pending", "new_version_required"].includes(status) ? "border-amber-300 bg-amber-50" : "border-neutral-200 bg-white"}`} data-testid="dpa-status">
        <div className="font-semibold">Stato: {STATUS_LABEL[status] || status}</div>
        {status === "not_configured" && <div className="text-neutral-600 mt-1">Non è ancora stata pubblicata una versione dell'accordo: nessuna azione richiesta ora. Questo stato non equivale a "accettato".</div>}
        {status === "not_required" && <div className="text-neutral-600 mt-1">L'accordo riguarda solo i saloni con piano Pro, per i quali Marketing Parrucchieri lavora e pubblica contenuti per conto del salone.</div>}
        {data.active_version && (
          <div className="text-xs text-neutral-500 mt-1">Versione attiva: {v.version_id} · pubblicata {fmtRome(v.published_at)} · SHA-256 {v.sha256.slice(0, 16)}… {v.is_test && <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-red-700 font-semibold">VERSIONE FITTIZIA DI TEST</span>}</div>
        )}
      </div>

      {v && (
        <div className="rounded-xl border border-neutral-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
            <div className="font-semibold">{v.title}</div>
            <Button size="sm" variant="outline" onClick={() => download("/dpa/agreement/download", `accordo-art28-${v.version_id}.md`)} data-testid="dpa-download-btn"><Download className="h-4 w-4 mr-1" /> Scarica il testo integrale</Button>
          </div>
          <pre className="max-h-[480px] overflow-auto whitespace-pre-wrap p-4 text-[13px] leading-relaxed font-sans" data-testid="dpa-fulltext">{v.body_md}{(v.attachments || []).map((a) => `\n\n--- Allegato: ${a.name} ---\n\n${a.content}`).join("")}</pre>
        </div>
      )}

      {canAccept && (
        <div className="rounded-xl border border-neutral-200 bg-white p-4 space-y-4" data-testid="dpa-accept-form">
          <div className="font-semibold">Accettazione per conto del salone</div>
          <div className="text-xs text-neutral-600">Anagrafica legale del Titolare (campi distinti dall'insegna "{data.portal_salon_name || "—"}" usata nel portale): viene confermata da te, validata e congelata nella copia accettata.</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input placeholder="Ragione sociale / ditta" value={form.legal.legal_name} onChange={updLegal("legal_name")} data-testid="dpa-legal-name" />
            <Input placeholder="Partita IVA (11 cifre) o Codice fiscale" value={form.legal.piva} onChange={updLegal("piva")} data-testid="dpa-piva" />
            <Input placeholder="Indirizzo sede legale (via e numero civico)" value={form.legal.legal_address} onChange={updLegal("legal_address")} data-testid="dpa-legal-address" />
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="CAP" value={form.legal.legal_zip} onChange={updLegal("legal_zip")} data-testid="dpa-legal-zip" />
              <Input placeholder="Città" value={form.legal.legal_city} onChange={updLegal("legal_city")} data-testid="dpa-legal-city" />
              <Input placeholder="Prov." maxLength={2} value={form.legal.legal_province} onChange={updLegal("legal_province")} data-testid="dpa-legal-province" />
            </div>
            <Input placeholder="Nome" value={form.first_name} onChange={upd("first_name")} disabled={!!data.delegation} data-testid="dpa-first-name" />
            <Input placeholder="Cognome" value={form.last_name} onChange={upd("last_name")} disabled={!!data.delegation} data-testid="dpa-last-name" />
            <select className="h-10 rounded-md border border-neutral-200 bg-white px-3 text-sm" value={form.qualifica} onChange={upd("qualifica")} disabled={!!data.delegation} data-testid="dpa-qualifica">
              <option value="legale_rappresentante">Legale rappresentante</option>
              <option value="delegato">Delegato dal legale rappresentante</option>
            </select>
            {form.qualifica === "delegato" && <Input placeholder="Riferimento delega (es. delega scritta del 01/09/2026)" value={form.delegation_reference} onChange={upd("delegation_reference")} disabled={!!data.delegation} data-testid="dpa-delegation-ref" />}
          </div>
          {form.qualifica === "delegato" && !delegationVerified && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="dpa-delegation-box">
              {delegationPending ? "Richiesta di verifica della delega inviata: lo staff la controllerà prima di consentire l'accettazione. Nessuna accettazione è stata registrata." : "Chi accetta come delegato deve prima far verificare la delega dallo staff: invia la richiesta con il riferimento alla delega. Solo dopo la verifica potrai accettare (con data effettiva del tuo clic)."}
              {!delegationPending && <div className="mt-2"><Button size="sm" variant="outline" disabled={busy || !form.first_name || !form.last_name || form.delegation_reference.length < 5} onClick={requestDelegation} data-testid="dpa-delegation-request-btn">Invia richiesta di verifica delega</Button></div>}
            </div>
          )}
          {(form.qualifica === "legale_rappresentante" || delegationVerified) && (
            <div className="space-y-3">
              <Button variant="outline" size="sm" onClick={doPreview} disabled={busy} data-testid="dpa-preview-btn">Genera anteprima del documento compilato</Button>
              {preview && (
                <div className="rounded-lg border border-neutral-200" data-testid="dpa-preview">
                  <div className="border-b px-3 py-2 text-xs text-neutral-600">Questo è esattamente il documento che verrà congelato (SHA-256 <span className="font-mono">{preview.compiled_sha256.slice(0, 16)}…</span>). La data/ora di accettazione sarà quella registrata dal server al momento del clic e comparirà nella ricevuta.</div>
                  <pre className="max-h-72 overflow-auto whitespace-pre-wrap p-3 text-[12px] font-sans" data-testid="dpa-preview-text">{preview.compiled_text}</pre>
                </div>
              )}
              <Input type="password" placeholder="Conferma la tua password (ri-autenticazione)" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} autoComplete="current-password" data-testid="dpa-password" />
            </div>
          )}
          <label className="flex items-start gap-3 text-sm" data-testid="dpa-authority-label">
            <Checkbox checked={form.authority_declared} onCheckedChange={(c) => setForm((f) => ({ ...f, authority_declared: c === true }))} data-testid="dpa-authority-checkbox" className="mt-0.5" />
            <span>{data.declarations.authority}</span>
          </label>
          <label className="flex items-start gap-3 text-sm" data-testid="dpa-contract-label">
            <Checkbox checked={form.contract_accepted} onCheckedChange={(c) => setForm((f) => ({ ...f, contract_accepted: c === true }))} data-testid="dpa-contract-checkbox" className="mt-0.5" />
            <span>{data.declarations.contract}</span>
          </label>
          <p className="text-xs text-neutral-500">L'accordo vale dal momento della conferma (data e ora registrate dal server). Non è una firma digitale qualificata né un consenso privacy: è un'accettazione contrattuale per click con dichiarazione dei poteri. Se non sei autorizzato a impegnare il salone, non procedere: chiedi al legale rappresentante oppure <a className="underline" href="mailto:info@marketingparrucchieri.info">contatta l'assistenza</a>.</p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={submit} disabled={busy || !preview || !form.authority_declared || !form.contract_accepted || !form.password || (form.qualifica === "delegato" && !delegationVerified)} data-testid="dpa-accept-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />} Accetto l'accordo per conto del salone
            </Button>
            <Button variant="outline" asChild data-testid="dpa-not-authorized-btn"><a href="mailto:info@marketingparrucchieri.info?subject=Accordo%20art.%2028%20-%20non%20sono%20autorizzato">Non sono autorizzato / contatta assistenza</a></Button>
          </div>
        </div>
      )}

      {acceptances.length > 0 && (
        <div className="rounded-xl border border-neutral-200 bg-white p-4" data-testid="dpa-acceptances">
          <div className="font-semibold mb-2">Le tue accettazioni</div>
          <ul className="space-y-2 text-sm">
            {acceptances.map((a) => (
              <li key={a.acceptance_id} className="flex flex-wrap items-center justify-between gap-2 border-t pt-2 first:border-0 first:pt-0" data-testid={`dpa-acceptance-${a.acceptance_id}`}>
                <span>Versione <b>{a.version_id}</b> · accettata il {fmtRome(a.accepted_at)} da {a.identity.referente_nome} {a.identity.referente_cognome} ({a.identity.qualifica}) per {a.identity.legal_name} · hash {a.compiled_sha256.slice(0, 12)}…</span>
                <span className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => download(`/dpa/receipt/${a.acceptance_id}?format=html`, `ricevuta-accordo-${a.acceptance_id}.html`)} data-testid={`dpa-receipt-${a.acceptance_id}`}><Download className="h-3.5 w-3.5 mr-1" /> Ricevuta</Button>
                  <Button size="sm" variant="ghost" onClick={() => download(`/dpa/receipt/${a.acceptance_id}?format=md`, `accordo-compilato-${a.acceptance_id}.md`)}>Copia accettata</Button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
